import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import React, { useState } from 'react'

import { cn } from '../../lib/utils'
import { PASSWORD_MANAGER_IGNORE_ATTRIBUTES } from './password-manager-ignore'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  className?: string
  /**
   * `mono` switches the input font to JetBrains Mono — useful for IDs,
   * coordinates, hashes, and other machine-generated values.
   */
  variant?: 'default' | 'mono'
  /**
   * Decorative icon rendered inside the field, before the text. The input
   * pads itself to clear it and the icon is layered above the input's
   * opaque background — no manual `pl-*`/`z-*` at the call site.
   *
   * @example
   * <Input
   *   leadingIcon={<MagnifyingGlassIcon className="h-3.5 w-3.5" />}
   *   placeholder="Filter projects…"
   * />
   */
  leadingIcon?: React.ReactNode
}

/**
 * The styled text field with no opinion about password managers.
 *
 * Internal to `@droidmap/frontend-ui` — deliberately not exported from the
 * package index. App code chooses between the two public wrappers:
 * {@link Input} (ignored by password-manager extensions, the default for
 * every non-credential field) and `CredentialInput` (fillable, with a
 * required, typed `autoComplete`). Making the choice a choice of
 * *component* rather than a prop means a call site cannot silently end up
 * with a fillable field by forgetting or re-spreading an attribute.
 */
const BaseInput = React.forwardRef<HTMLInputElement, InputProps>(
  (props, ref) => {
    const {
      className,
      label,
      type,
      autoFocus,
      variant = 'default',
      leadingIcon,
      ...rest
    } = props
    const id =
      props.id ||
      props.name ||
      `input-${label?.toLowerCase().replace(/\s+/g, '-')}`

    // Show/hide password state
    const [showPassword, setShowPassword] = useState(false)
    const isPassword = type === 'password'
    const inputType = isPassword && showPassword ? 'text' : type

    return (
      <div className="flex flex-col justify-center">
        {label && (
          <label
            htmlFor={id}
            className="text-micro text-text-mute mb-2 block leading-none"
          >
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          {leadingIcon && (
            <span
              aria-hidden="true"
              className="text-text-mute pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2"
            >
              {leadingIcon}
            </span>
          )}
          {isPassword && (
            <button
              type="button"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute left-0 pl-2 flex items-center justify-center h-full text-text-mute hover:text-text focus:outline-hidden z-10 transition-colors"
              style={{ height: '100%' }}
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? (
                <EyeSlashIcon className="h-4 w-4" />
              ) : (
                <EyeIcon className="h-4 w-4" />
              )}
            </button>
          )}
          <input
            {...rest}
            id={id}
            ref={ref}
            type={inputType}
            autoFocus={autoFocus}
            data-autofocus={autoFocus || undefined}
            className={cn(
              'block w-full rounded-md border border-border-strong bg-bg-2 text-[13px] text-text px-3.5 py-2.5 placeholder:text-text-mute focus:outline-hidden focus:border-accent-tint focus:ring-[3px] focus:ring-accent-soft transition-[border-color,box-shadow] duration-150 sm:leading-6',
              // Disabled — flatten bg, mute text, drop opacity, block clicks.
              'disabled:opacity-50 disabled:bg-bg disabled:text-text-mute disabled:border-border disabled:cursor-not-allowed',
              variant === 'mono' && 'font-mono text-[12px]',
              isPassword ? 'pl-8' : '',
              leadingIcon ? 'pl-9' : '',
              className,
            )}
          />
        </div>
      </div>
    )
  },
)

BaseInput.displayName = 'BaseInput'

/**
 * The default text field. Password-manager extensions are told to ignore it
 * via every vendor's documented `data-*` opt-out
 * ({@link PASSWORD_MANAGER_IGNORE_ATTRIBUTES}), present from the first
 * render. Almost every input in the app — search boxes, names, coordinates,
 * confirmation codes — is not a credential, so this is the right default;
 * the handful of sign-in / sign-up / password-reset fields that a manager
 * *should* fill use `CredentialInput` instead.
 *
 * The ignore attributes are applied after the caller's props, so passing
 * e.g. `data-lpignore="false"` has no effect. That is deliberate: opting a
 * field back in is done by choosing `CredentialInput`, which also forces an
 * explicit, typed `autoComplete` — the omission that let #2405 happen.
 *
 * `autoComplete` is still honoured here and still matters: it addresses the
 * browser's own autofill (a separate layer), e.g. `"one-time-code"` opts a
 * code field into iOS/Android SMS-OTP fill while the extension stays away.
 *
 * @example
 * <Input label="Confirmation code" autoComplete="one-time-code" />
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => (
  <BaseInput {...props} ref={ref} {...PASSWORD_MANAGER_IGNORE_ATTRIBUTES} />
))

Input.displayName = 'Input'

export { BaseInput, Input, PASSWORD_MANAGER_IGNORE_ATTRIBUTES }
export default Input
